import { useEffect, useState } from 'react';
import { useAuthedApi } from '../../lib/useAuthedApi';

type ApiKey = {
  id: string;
  name: string;
  last_used_at: string | null;
  created_at: string;
  revoked_at: string | null;
};

type CreateResp = ApiKey & { key: string };

export default function ApiKeys() {
  const apiFn = useAuthedApi();
  const [items, setItems] = useState<ApiKey[]>([]);
  const [name, setName] = useState('');
  const [reveal, setReveal] = useState<CreateResp | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = () =>
    apiFn<ApiKey[]>('/api-keys')
      .then(setItems)
      .catch((e) => setErr(e.message));

  useEffect(() => {
    void load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function create() {
    setErr(null);
    if (!name) {
      setErr('name_required');
      return;
    }
    try {
      const r = await apiFn<CreateResp>('/api-keys', { body: { name } });
      setReveal(r);
      setName('');
      await load();
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  async function revoke(id: string) {
    if (!confirm('Revoke this API key? Consumers using it will start failing.')) return;
    await apiFn(`/api-keys/${id}`, { method: 'DELETE' }).then(load);
  }

  return (
    <div>
      <div className="card">
        <h2 style={{ marginTop: 0 }}>Create an API key</h2>
        <p className="muted">
          Shown <strong>once</strong>. Store it somewhere safe — we keep only a hash.
        </p>
        <label>
          <span>Name (your reference)</span>
          <input value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        {err && <p className="err">{err}</p>}
        <button className="primary" onClick={create}>
          Create
        </button>
      </div>

      {reveal && (
        <div className="card" style={{ borderColor: 'var(--accent)' }}>
          <h2 style={{ marginTop: 0 }}>Your new key — copy it now</h2>
          <code style={{ wordBreak: 'break-all', display: 'block', marginBottom: '0.75rem' }}>
            {reveal.key}
          </code>
          <button onClick={() => setReveal(null)}>I&apos;ve copied it</button>
        </div>
      )}

      <table>
        <thead>
          <tr>
            <th>Name</th>
            <th>Last used</th>
            <th>Created</th>
            <th>Status</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {items.map((k) => (
            <tr key={k.id}>
              <td>{k.name}</td>
              <td>{k.last_used_at ?? '—'}</td>
              <td>{k.created_at}</td>
              <td>{k.revoked_at ? 'Revoked' : 'Active'}</td>
              <td>
                {!k.revoked_at && (
                  <button className="danger" onClick={() => revoke(k.id)}>
                    Revoke
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
